import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mic, MicOff, Loader2, Sparkles, CheckCircle2, AlertTriangle, X, RotateCcw, Play, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * VoiceJournal — registra una nota vocale post-call e la usa per:
 *  1) Trascrivere via Whisper (italiano)
 *  2) Analizzare via Claude → JSON {summary, esito, nuovoStato, nuovaDataRecall, noteAggiuntive, promemoriaSuggerito}
 *  3) Auto-update del cliente: append timeline + opz. cambia stato/data/promemoria
 *
 * Props: clientId, onApplied()
 */

function MIME_supported() {
  if (typeof MediaRecorder === "undefined") return null;
  // Preferisci webm/opus → smallest + supportato chrome/firefox; fallback mp4/m4a su iOS Safari
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/mpeg",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

export default function VoiceJournal({ clientId, onApplied }) {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(""); // "transcribe" | "analyze" | "apply" | ""
  const [transcript, setTranscript] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState("");

  const mediaRecRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setSupported(false);
    }
    return () => {
      try { timerRef.current && clearInterval(timerRef.current); } catch {}
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl("");
    setTranscript("");
    setAnalysis(null);
    setApplied(null);
    setError("");
    setSecs(0);
  };

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mime = MIME_supported() || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch {}
        const type = mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
      };
      rec.start();
      setRecording(true);
      setSecs(0);
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch (e) {
      setError("Permesso microfono negato. Abilita l'accesso al microfono nelle impostazioni del browser.");
      setSupported(false);
    }
  };

  const stop = () => {
    try { timerRef.current && clearInterval(timerRef.current); } catch {}
    try { mediaRecRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const submit = async (apply = true) => {
    if (!audioBlob) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      const ext = (audioBlob.type || "audio/webm").includes("mp4") ? "mp4" : (audioBlob.type || "audio/webm").includes("mpeg") ? "mp3" : "webm";
      fd.append("audio", audioBlob, `note.${ext}`);
      fd.append("client_id", clientId);
      fd.append("apply", apply ? "true" : "false");
      setStage("transcribe");
      const r = await api.post("/voice/journal", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setStage("");
      setTranscript(r.data?.transcript || "");
      setAnalysis(r.data?.analysis || null);
      setApplied(r.data?.applied || null);
      if (apply && r.data?.applied?.update_appended) {
        if (r.data?.ai_ok === false) {
          toast.warning(r.data?.ai_error || "Nota salvata con la sola trascrizione (AI non disponibile)", { duration: 7000 });
        } else {
          toast.success("Cliente aggiornato con la nota vocale");
        }
        onApplied?.();
      } else if (!apply) {
        toast.info("Anteprima generata (nessuna modifica salvata)");
      }
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e?.message || "Errore nella registrazione";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  const fmtSec = (s) => `${String(Math.floor(s / 60)).padStart(1, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (!supported) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          <div className="text-sm">Microfono non disponibile su questo browser. Usa Chrome/Safari su HTTPS.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="voice-journal">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 rounded-sm bg-[#0A192F] text-[#B7955B] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <h3 className="font-serif text-lg text-slate-900">Voice Notes AI</h3>
          </div>
          <p className="text-xs text-slate-500 max-w-xl">
            Registra una nota vocale dopo la chiamata. L'AI trascrive, sintetizza l'esito e aggiorna automaticamente stato, recall e timeline del cliente.
          </p>
        </div>
        {(audioBlob || transcript) && (
          <button
            onClick={reset}
            className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
            data-testid="voice-reset-btn"
          >
            <X className="w-3 h-3" /> Pulisci
          </button>
        )}
      </div>

      {/* Recorder */}
      {!audioBlob && (
        <div className="border border-dashed border-slate-300 rounded-sm p-6 flex flex-col items-center gap-4 bg-slate-50">
          {!recording ? (
            <button
              onClick={start}
              data-testid="voice-record-btn"
              className="w-20 h-20 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-lg transition active:scale-95"
              title="Avvia registrazione"
            >
              <Mic className="w-9 h-9" />
            </button>
          ) : (
            <>
              <div className="relative">
                <button
                  onClick={stop}
                  data-testid="voice-stop-btn"
                  className="w-20 h-20 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-lg transition active:scale-95 animate-pulse"
                  title="Stop"
                >
                  <MicOff className="w-9 h-9" />
                </button>
                <span className="absolute inset-0 rounded-full ring-4 ring-rose-300 animate-ping pointer-events-none" />
              </div>
              <div className="font-mono text-2xl text-slate-900 tabular-nums">{fmtSec(secs)}</div>
              <div className="text-[11px] uppercase tracking-wider text-rose-600">In registrazione…</div>
            </>
          )}
          {!recording && (
            <div className="text-[11px] text-slate-500 text-center max-w-md">
              Premi il microfono per iniziare. Suggerito 30s-2min: dì <span className="italic">"Cliente interessato 15.000€ a 84 mesi, richiamare martedì"</span>.
            </div>
          )}
        </div>
      )}

      {/* Preview registrazione */}
      {audioBlob && !transcript && !busy && (
        <div className="space-y-3">
          <audio src={audioUrl} controls className="w-full" data-testid="voice-audio-preview" />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => submit(true)}
              data-testid="voice-submit-btn"
              className="bg-[#0A192F] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#0d2244] flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> Trascrivi e aggiorna cliente
            </button>
            <button
              onClick={() => submit(false)}
              className="border border-slate-300 text-sm px-4 py-2 rounded-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
              title="Solo anteprima senza salvare"
            >
              <Play className="w-4 h-4" /> Anteprima
            </button>
            <button
              onClick={reset}
              className="border border-slate-300 text-sm px-4 py-2 rounded-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
            >
              <RotateCcw className="w-4 h-4" /> Riregistra
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {busy && (
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm p-4">
          <Loader2 className="w-5 h-5 animate-spin text-[#B7955B]" />
          <div>
            <div className="text-sm text-slate-900 font-medium">
              {stage === "transcribe" ? "Trascrizione in corso…" : stage === "analyze" ? "Analisi AI…" : "Elaborazione…"}
            </div>
            <div className="text-xs text-slate-500">Potrebbe richiedere 5-15 secondi a seconda della durata della nota.</div>
          </div>
        </div>
      )}

      {/* Risultati */}
      {transcript && !busy && (
        <div className="space-y-4 animate-fade-in">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Trascrizione</div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm text-slate-700 italic" data-testid="voice-transcript">
              "{transcript}"
            </div>
          </div>

          {analysis && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-white border-2 border-[#B7955B] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#B7955B] mb-1">Sintesi AI</div>
                <div className="text-sm text-slate-900 font-medium">{analysis.summary || "—"}</div>
                {analysis.esito && <div className="text-xs text-slate-600 mt-1">{analysis.esito}</div>}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Aggiornamenti</div>
                <ul className="text-xs text-slate-700 space-y-1">
                  {analysis.nuovoStato && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                      Stato → <strong>{analysis.nuovoStato}</strong>
                    </li>
                  )}
                  {analysis.nuovaDataRecall && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                      Recall → <strong>{analysis.nuovaDataRecall}</strong>
                    </li>
                  )}
                  {analysis.promemoriaSuggerito?.when && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                      Promemoria: <strong>{analysis.promemoriaSuggerito.when}</strong>
                    </li>
                  )}
                  {!analysis.nuovoStato && !analysis.nuovaDataRecall && !analysis.promemoriaSuggerito?.when && (
                    <li className="text-slate-400">Nessuna modifica suggerita</li>
                  )}
                </ul>
              </div>
            </div>
          )}

          {analysis?.noteAggiuntive && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Note operative aggiunte alla timeline</div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-slate-800">
                {analysis.noteAggiuntive}
              </div>
            </div>
          )}

          {applied && applied.update_appended && (
            <div className="flex items-center gap-2 text-emerald-700 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Cliente aggiornato.
              {applied.stato_changed && <span className="ml-2 text-slate-600 text-slate-900">Stato {applied.stato_changed.from} → <strong >{applied.stato_changed.to}</strong></span>}
              {applied.recall_changed && <span className="ml-2 text-slate-600 text-slate-900">Recall → <strong >{applied.recall_changed.to}</strong></span>}
              {applied.reminder_id && <span className="ml-2 text-slate-600">+ promemoria creato</span>}
            </div>
          )}

          {applied && !applied.update_appended && analysis && (
            <div className="flex items-center gap-2 text-slate-700 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Anteprima — nessuna modifica salvata.
              <button
                onClick={() => submit(true)}
                className="ml-2 text-xs text-[#B7955B] hover:text-[#a0824a] flex items-center gap-1"
              >
                <Save className="w-3 h-3" /> Salva ora
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-sm p-3 text-sm text-rose-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
    </div>
  );
}
